import {CohortsExportLimits} from 'common/report/models/integrations/types';

import {
  BooleanOperator,
  ConditionTab,
  ConditionType,
  DatetimeOperator,
  FrequencyOperator,
  ListOperator,
  NumberOperator,
  StringOperator,
  Type,
} from './enums/builder-enums';
import {IntegrationType} from './enums/connect-enums';
import {Property, WindowUnit} from './enums/expression-enums';
import {FilterOperator} from './types/builder-state-types';

export const CONDITION_TYPE_TO_TAB_MAPPING = {
  [ConditionType.Cohorts]: ConditionTab.Cohorts,
  [ConditionType.Events]: ConditionTab.Events,
  [ConditionType.People]: ConditionTab.People,
};

export const LITERAL_NAMES = [
  `A`,
  `B`,
  `C`,
  `D`,
  `E`,
  `F`,
  `G`,
  `H`,
  `I`,
  `J`,
  `K`,
  `L`,
  `M`,
  `N`,
  `O`,
  `P`,
  `Q`,
  `R`,
  `S`,
  `T`,
  `U`,
  `V`,
  `W`,
  `X`,
  `Y`,
  `Z`,
];
export const MAX_LITERALS = LITERAL_NAMES.length;
export const MAX_CONDITIONS = 15;
export const LITERAL_NAME_REGEX = new RegExp(`[${LITERAL_NAMES.join(``)}]\\d*`, `g`);

export const DEFAULT_FREQUENCY_VALUE = 1;
export const ANY_EVENT = ``;
export const FIRST_EVENT = `$first_event`;
export const DEFAULT_EVENT = ANY_EVENT;

export const DEFAULT_RELATIVE_NUM_DAYS = 7;
export const DEFAULT_WINDOW_UNIT = WindowUnit.Day;

export const DEFAULT_PEOPLE_PROPERTY_OPTIONS = {
  operator: DatetimeOperator.Relative,
  property: `$created`,
  propertyType: Property.User,
  type: Type.Datetime,
  value: {
    window: {
      unit: WindowUnit.Day,
      value: 30,
    },
  },
};
export const MAX_LABEL_ENTRIES = 3;
export const SHOW_MORE_LIMIT = 5;
export const TABS_LIMIT_NORMAL_WIDTH = 4;
export const MAX_COHORT_NAME_LENGTH = 190;
export const FUNNEL_CLAUSE_OPERATOR: any = null;

export const FREQUENCY_OPERATOR_LIST = [
  FrequencyOperator.Between,
  FrequencyOperator.AtLeast,
  FrequencyOperator.GreaterThan,
  FrequencyOperator.LessThan,
  FrequencyOperator.EqualTo,
];

export const FILTER_TYPE_LIST = [Type.String, Type.Number, Type.Boolean, Type.Datetime, Type.List];

const STRING_FILTER_OPERATOR_LIST = [
  StringOperator.Contains,
  StringOperator.DoesNotContain,
  StringOperator.Equals,
  StringOperator.DoesNotEqual,
  StringOperator.Set,
  StringOperator.NotSet,
];

const NUMBER_FILTER_OPERATOR_LIST = [
  NumberOperator.Between,
  NumberOperator.AtLeast,
  NumberOperator.GreaterThan,
  NumberOperator.LessThan,
  NumberOperator.EqualTo,
  NumberOperator.NotEqualTo,
];

const DATETIME_FILTER_OPERATOR_LIST = [
  DatetimeOperator.Between,
  DatetimeOperator.After,
  DatetimeOperator.On,
  DatetimeOperator.RelativeBeforeInPast,
  DatetimeOperator.Relative,
  DatetimeOperator.RelativeFuture,
];

export const FILTER_OPERATORS: {[filter: string]: FilterOperator[]} = {
  [Type.String]: STRING_FILTER_OPERATOR_LIST,
  [Type.Number]: NUMBER_FILTER_OPERATOR_LIST,
  [Type.Datetime]: DATETIME_FILTER_OPERATOR_LIST,
  [Type.Boolean]: [BooleanOperator.True, BooleanOperator.False],
  [Type.List]: [ListOperator.Contains, ListOperator.DoesNotContain],
};

export const DEFAULT_FILTER_OPERATOR: {[filter: string]: FilterOperator} = {
  [Type.String]: StringOperator.Equals,
  [Type.Number]: NumberOperator.EqualTo,
  [Type.Datetime]: DatetimeOperator.Relative,
  [Type.Boolean]: BooleanOperator.True,
  [Type.List]: ListOperator.Contains,
};

export const EXPORT_INTEGRATIONS_INFO = {
  [IntegrationType.Braze]: {
    exportStartedMessage: `Your cohort export to Braze has begun.`,
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/braze`,
    helpLinkText: `Setting up the Mixpanel Cohort in Braze`,
    noUsersText: `There are no user profiles with a Braze external user id in this cohort`,
    tooltipText: `Only user profiles with a Braze external user id will be exported`,
  },
  [IntegrationType.Marketo]: {
    duplicateExternalInfoError: `This custom field has already been used`,
    exportStartedMessage: `Your cohort export to Marketo has begun.`,
    externalInfoInputData: {
      fieldColumnHeader: `Custom field name`,
      param: `marketo_custom_field_name`,
      placeholder: `Marketo Custom Field to update`,
      title: `Custom field name`,
    },
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/marketo` + `#marketo-integration-setup`,
    helpLinkText: `Setting up Custom Fields in Marketo`,
    maxExportUsers: 30000000,
    maxExportUsersText: `The maximum exportable cohort size is 30 million user profiles at this time.`,
    noUsersText: `There are no user profiles with $email in this cohort`,
    tooltipText: `Only user profiles with $email will be exported`,
  },
  [IntegrationType.UrbanAirship]: {
    duplicateExternalInfoError: `This tag has already been used`,
    exportStartedMessage: `Your cohort export to Airship has begun.`,
    externalInfoInputData: {
      fieldColumnHeader: `Tag name`,
      param: `tag`,
      placeholder: `Airship tag to add`,
      title: `Airship tag`,
    },
    helpLink:
      `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/airship` + `#setting-up-the-urban-airship-integration`,
    helpLinkText: `Setting up the Mixpanel Tag Group in Airship`,
    maxExportUsers: 15000000,
    maxExportUsersText: `The maximum exportable cohort size is 15 million user profiles at this time.`,
    noUsersText: `There are no user profiles with an Airship Channel ID or a Named User ID in this cohort`,
    tooltipText: `Only user profiles with an Airship Channel ID or a Named User ID will be exported`,
  },
  [IntegrationType.FacebookAds]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/facebook-ads`,
    helpLinkText: `Setting up the Facebook Ads Integration`,
    exportStartedMessage: `Your cohort export to Facebook Ads has begun.`,
    tooltipText: `Only user profiles with an email, a phone number, a first name, and a last name will be exported`,
  },
  [IntegrationType.Iterable]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/iterable`,
    helpLinkText: `Setting up the Iterable Integration`,
    exportStartedMessage: `Your cohort export to Iterable has begun.`,
    tooltipText: `Only user profiles with an email will be exported`,
  },
  [IntegrationType.GoogleAds]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/google-ads`,
    helpLinkText: `Setting up the Google Adwords Integration`,
    exportStartedMessage: `Your cohort export to Google Adwords has begun.`,
    tooltipText: `Only user profiles with an email and a phone number will be exported`,
  },
  [IntegrationType.TestPartnerWebhookIntegration]: {
    helpLink: `www.google.com`,
    helpLinkText: `No help article for this Integration yet`,
    exportStartedMessage: `Your cohort export to Test Partner Webhook Integration has begun.`,
    tooltipText: `Only user profiles with an email will be exported`,
  },
  [IntegrationType.MoengageWebhook]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/moengage`,
    helpLinkText: `Setting up the MoEngage integration`,
    exportStartedMessage: `Your cohort export to Moengage has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.KameleoonWebhook]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/kameleoon`,
    helpLinkText: `Setting up the Kameleoon integration`,
    exportStartedMessage: `Your cohort export to Kameleoon has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.MailchimpWebhook]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/mailchimp`,
    helpLinkText: `Setting up the Mailchimp integration`,
    exportStartedMessage: `Your cohort export to Mailchimp has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.VwoWebhook]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/vwo`,
    helpLinkText: `Setting up the VWO integration`,
    exportStartedMessage: `Your cohort export to VWO has begun.`,
    tooltipText: `Only user profiles with a "$vwo_user_id" property will be exported`,
  },
  [IntegrationType.TaplyticsWebhook]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/taplytics`,
    helpLinkText: `Setting up the Taplytics integration`,
    exportStartedMessage: `Your cohort export to Taplytics has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.LeanplumWebhook]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/leanplum`,
    helpLinkText: `Setting up the Leanplum integration`,
    exportStartedMessage: `Your cohort export to Leanplum has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.InsiderWebhook]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/insider`,
    helpLinkText: `Setting up the Insider integration`,
    exportStartedMessage: `Your cohort export to Insider has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.AppcuesWebhook]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/appcues`,
    helpLinkText: `Setting up the Appcues integration`,
    exportStartedMessage: `Your cohort export to Appcues has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.ChameleonWebhook]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/chameleon`,
    helpLinkText: `Setting up the Chameleon integration`,
    exportStartedMessage: `Your cohort export to Chameleon has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.OnesignalWebhook]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/onesignal`,
    helpLinkText: `Setting up the OneSignal integration`,
    exportStartedMessage: `Your cohort export to OneSignal has begun.`,
    tooltipText: `Only user profiles with a "$onesignal_user_id" property will be exported`,
  },
  [IntegrationType.WebengageWebhook]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/webengage`,
    helpLinkText: `Setting up the WebEngage integration`,
    exportStartedMessage: `Your cohort export to WebEngage has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.XtremepushWebhook]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/xtremepush`,
    helpLinkText: `Setting up the Xtremepush integration`,
    exportStartedMessage: `Your cohort export to Xtremepush has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.ApptimizeWebhook]: {
    helpLink: ``,
    helpLinkText: `Setting up the Apptimize integration`,
    exportStartedMessage: `Your cohort export to Apptimize has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.ClevertapWebhook]: {
    helpLink: `https://docs.clevertap.com/docs/mixpanel-integration`,
    helpLinkText: `Setting up the CleverTap integration`,
    exportStartedMessage: `Your cohort export to CleverTap has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.CustomWebhook]: {
    helpLink: `https://developer.mixpanel.com/docs/cohort-webhooks`,
    helpLinkText: `Setting up the Custom Webhook integration`,
    exportStartedMessage: `Your cohort export to your Custom Webhook integration has begun.`,
    tooltipText: `Only identified user profiles will be exported`,
  },
  [IntegrationType.SegmentWebhook]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/segment`,
    helpLinkText: `Setting up the Segment integration`,
    exportStartedMessage: `Your cohort export to your Segment integration has begun.`,
    tooltipText: `All user profiles will be exported`,
  },
  [IntegrationType.Salesforcemc]: {
    helpLink: `https://docs.mixpanel.com/docs/other-bits/cohort-syncs/salesforce-marketing-cloud`,
    helpLinkText: `Setting up the Salesforce Marketing Cloud integration`,
    exportStartedMessage: `Your cohort export to your Salesforce Marketing Cloud integration has begun.`,
    tooltipText: `All user profiles with the key used at setup will be exported`,
  },
  [IntegrationType.LaunchDarklyWebhook]: {
    helpLink: ``,
    helpLinkText: `Setting up the LaunchDarkly integration`,
    exportStartedMessage: `Your cohort export to your LaunchDarkly integration has begun.`,
    tooltipText: `All user profiles will be exported`,
  },
  [IntegrationType.BlitzllamaWebhook]: {
    helpLink: ``,
    helpLinkText: `Setting up the Blitzllama integration`,
    exportStartedMessage: `Your cohort export to your Blitzllama integration has begun.`,
    tooltipText: `Only user profiles with a "$blitzllama_user_id" property will be exported`,
  },
  [IntegrationType.ProductboardWebhook]: {
    helpLink: ``,
    helpLinkText: `Setting up the Productboard integration`,
    exportStartedMessage: `Your cohort export to your Productboard integration has begun.`,
    tooltipText: `Only user profiles with an Email property will be exported`,
  },
  [IntegrationType.ABTastyWebhook]: {
    helpLink: `https://abtasty.zendesk.com/hc/en-us/articles/4680111637660`,
    helpLinkText: `Setting up the ABTasty integration`,
    exportStartedMessage: `Your cohort export to your ABTasty integration has begun.`,
    tooltipText: `All user profiles will be exported`,
  },
  [IntegrationType.FlagshipWebhook]: {
    helpLink: `https://flagship.zendesk.com/hc/en-us/articles/4506406524828--Retrieving-your-third-party-tools-audiences-in-Flagship`,
    helpLinkText: `Setting up the Flagship integration`,
    exportStartedMessage: `Your cohort export to your Flagship integration has begun.`,
    tooltipText: `All user profiles will be exported`,
  },
  [IntegrationType.PushwooshWebhook]: {
    helpLink: ``,
    helpLinkText: `Setting up the Pushwoosh integration`,
    exportStartedMessage: `Your cohort export to your Pushwoosh integration has begun.`,
    tooltipText: `All user profiles will be exported`,
  },
};

export const EXPORT_TOGGLE_OPTIONS = [
  {
    text: {
      [IntegrationType.Braze]: `All users in the cohort will be updated one time.`,
      [IntegrationType.Marketo]: `All users in the cohort will be updated with the custom field one time.`,
      [IntegrationType.UrbanAirship]: `All users in the cohort will be updated with the tag one time.`,
      [IntegrationType.FacebookAds]: `All users in the cohort will be uploaded to the Business Manager, replacing the old one.`,
      [IntegrationType.GoogleAds]: `All users in the cohort will be uploaded as a Custom Audience, replacing the old one.`,
      [IntegrationType.Iterable]: `All users in the cohort will be uploaded as an Iterable List, replacing the old one.`,
      [IntegrationType.TestPartnerWebhookIntegration]: `All users in the cohort will exported to the Webhook destination of choice.`,
      [IntegrationType.MoengageWebhook]: `All exported users will be sent as a MoEngage custom segment.`,
      [IntegrationType.LeanplumWebhook]: `Users in the cohort that match successfully into Leanplum will be uploaded as a Leanplum Audience, replacing the old one.`,
      [IntegrationType.ChameleonWebhook]: `All exported users will be sent as a Chameleon custom segment.`,
      [IntegrationType.InsiderWebhook]: `All exported users will be sent as an Insider custom segment.`,
      [IntegrationType.AppcuesWebhook]: `All exported users will be sent as an Appcues custom segment.`,
      [IntegrationType.OnesignalWebhook]: `All exported users will be sent as a OneSignal custom segment.`,
      [IntegrationType.KameleoonWebhook]: `All exported users will be sent as a Kameleoon custom segment.`,
      [IntegrationType.MailchimpWebhook]: `All exported users will be sent as a Mailchimp custom segment.`,
      [IntegrationType.TaplyticsWebhook]: `All exported users will be sent as a Taplytics custom segment.`,
      [IntegrationType.VwoWebhook]: `All exported users will be sent as a VWO custom segment.`,
      [IntegrationType.WebengageWebhook]: `All exported users will be sent as a WebEngage custom segment.`,
      [IntegrationType.XtremepushWebhook]: `All exported users will be sent as an Xtremepush custom segment.`,
      [IntegrationType.ApptimizeWebhook]: `All exported users will be sent as an Apptimize custom segment.`,
      [IntegrationType.ClevertapWebhook]: `All exported users will be sent as an CleverTap custom segment.`,
      [IntegrationType.CustomWebhook]: `All users will be exported to Custom Webhook.`,
      [IntegrationType.SegmentWebhook]: `All users will be exported to Segment.`,
      [IntegrationType.Salesforcemc]: `All users with the key used during setup will be exported to Salesforce Marketing Cloud.`,
      [IntegrationType.LaunchDarklyWebhook]: `All users will be exported to LaunchDarkly.`,
      [IntegrationType.BlitzllamaWebhook]: `All users will be exported to Blitzllama.`,
      [IntegrationType.ProductboardWebhook]: `All users will be exported to Productboard.`,
      [IntegrationType.ABTastyWebhook]: `All users will be exported to ABTasty`,
      [IntegrationType.FlagshipWebhook]: `All users will be exported to Flagship`,
      [IntegrationType.PushwooshWebhook]: `All users will be exported to PushWoosh`,
    },
    limitText: (_: CohortsExportLimits) => `Unlimited one-time syncs`,
    title: `One-time export`,
    type: `static`,
  },
  {
    text: {
      [IntegrationType.Braze]: `Your Braze cohort will be updated every 15 minutes to match users in Mixpanel cohort.`,
      [IntegrationType.Marketo]: `The custom field will be updated every 2 hours to match users in the cohort.`,
      [IntegrationType.UrbanAirship]: `Tag will be updated every 15 minutes to match users in the cohort.`,
      [IntegrationType.FacebookAds]: `Your Facebook Custom Audience will be updated every 2 hours to match users in this cohort.`,
      [IntegrationType.GoogleAds]: `Your Google Custom Audience will be updated every 2 hours to match users in this cohort.`,
      [IntegrationType.Iterable]: `Your Iterable List will be updated every 15 minutes to match users in this cohort.`,
      [IntegrationType.TestPartnerWebhookIntegration]: `All users in the cohort will exported every 2 hours to the Webhook destination of choice.`,
      [IntegrationType.MoengageWebhook]: `Your MoEngage custom segment will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.LeanplumWebhook]: `Your Leanplum Audience will be updated every 15 minutes with users in this cohort who successfully match into Leanplum.`,
      [IntegrationType.ChameleonWebhook]: `Your Chameleon custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.InsiderWebhook]: `Your Insider custom segment will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.AppcuesWebhook]: `Your Appcues custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.OnesignalWebhook]: `Your OneSignal custom segment will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.KameleoonWebhook]: `Your Kameleoon custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.MailchimpWebhook]: `Your Mailchimp custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.TaplyticsWebhook]: `Your Taplytics custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.VwoWebhook]: `Your VWO custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.WebengageWebhook]: `Your WebEngage custom segment will be updated every 2 hours with the most current set of users exported from the cohort.`,
      [IntegrationType.XtremepushWebhook]: `Your Xtremepush custom segment will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.ApptimizeWebhook]: `Your Apptimize custom segment will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.ClevertapWebhook]: `Your CleverTap custom segment will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.CustomWebhook]: `Your Custom Webhook will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.SegmentWebhook]: `Your Segment integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.Salesforcemc]: `Your Salesforce Marketing Cloud integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.LaunchDarklyWebhook]: `Your LaunchDarkly integration will be updated every 30 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.BlitzllamaWebhook]: `Your Blitzllama integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.ProductboardWebhook]: `Your Productboard integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.PushwooshWebhook]: `Your Pushwoosh integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.ABTastyWebhook]: `Your ABTasty integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
      [IntegrationType.FlagshipWebhook]: `Your Flagship integration will be updated every 15 minutes with the most current set of users exported from the cohort.`,
    },
    limitText: (cel: CohortsExportLimits) => `${cel.numRecurringExports} of ${cel.limit} recurring syncs in use.`,
    title: `Recurring sync`,
    type: `dynamic`,
  },
];
